import { RRule } from "rrule";
import { generateUID } from "verdi-common/utils/HelperFunctions";
import { RepeatingIrrigationAPI } from "../../APICommands/index.js";
import { EventHandler } from "../../HelperFunctions/index.js";
import { addEntry, deleteEntry, MasterIndex } from "../AMasterIndex.js";
import { Zone } from "../Zone.js";
import { HashedDaySchedule } from "./HashBasedScheduleTracker.js";
import { IrrigationEventProto } from "./IrrigationEventProto.js";
import { IrrigationSet } from "./IrrigationSet.js";
import { SetIrrigationEvent } from "./SetIrrigationEvent.js";

export class RepeatingIrrigationEvent extends IrrigationEventProto {
    static onNewRepeatingIrrigationEvent = new EventHandler();
    /**
     * For each zoneID that is irrigated as part of this event, the amount that it should be irrigated relative
     * to the default duration. If the default duration is 1 hr, and a particular zone has a relativeIrrigation of 0.5,
     * will be watered for 30 minutes.
     * @type {Object.<string, Number>}
     */
    get zoneWateringPercentages() {
        return this._zoneWateringPercentages;
    }
    set zoneWateringPercentages(v) {
        if (v) {
            this._zoneWateringPercentages = v;
        } else {
            throw new Error("Cannot set zone watering percentages to null or undefined");
        }
    }

    _zoneWateringPercentages = {};
    /**
     * For each zoneID that is irrigated as part of this event, the amount that it's start times
     * should be offset relative to the rrule start time. Mesured in ms.
     * @type {Object.<string, Number>}
     */
    zoneIrrigationOffsets = {};
    type: "single" | "repeating" | "csv" | "set" = "repeating";
    /**
     *
     * @type {RRule}
     */
    rule: RRule = undefined;

    /**
     * The default duration of irrigation, modulated by the relativeIrrigationPerZone
     * @return {undefined | Number}
     */
    get defaultDuration() {
        return this.duration;
    }
    /**
     * The default duration of irrigation, modulated by the relativeIrrigationPerZone
     * @type {undefined | Number}
     */
    set defaultDuration(v) {
        this.duration = v;
    }

    /**
     *
     * @type {Object<string, {instances: IrrigationEventProto[], duration: Number, endScope: Date, startScope: Date}>}
     */
    eventInstances = {};

    onChange = new EventHandler({}, 250);

    linkedIrrigationSetId = undefined;

    /**
     *
     * @type {IrrigationSet}
     */
    linkedIrrigationSet = undefined;
    /**
     *
     * @return {Promise<RepeatingIrrigationEvent>}
     * @constructor
     * @param zoneWateringPercentages
     * @param duration duration in milliseconds
     * @param rrule
     */
    static async createNew(
        zoneWateringPercentages,
        duration,
        rrule,
        group = undefined,
        zoneIrrigationOffsets,
        options,
    ) {
        const result = await RepeatingIrrigationAPI.addOne(
            zoneWateringPercentages,
            duration,
            rrule.toString(),
            group,
            zoneIrrigationOffsets,
            options,
        );
        return new RepeatingIrrigationEvent(result);
    }

    constructor(json, manualLoad = false) {
        super(json, true, false);
        this.dbID = json._id;
        this.rule = RRule.fromString(json.rrule);
        this.defaultDuration = json.defaultDuration;
        this.zoneIrrigationOffsets = json.zoneIrrigationOffsets || {};
        this.startTime = new Date(this.rule.after(new Date(Date.now() - 1000 * 60 * 60 * 24 * 360 * 2)));
        this.category = "repeatingEvent";
        this.zoneWateringPercentages = Object.assign({}, json.zoneWateringPercentages || {});

        if (json.irrigationSet) {
            this.linkedIrrigationSetId = json.irrigationSet;
        }
        if (!manualLoad) {
            this.load().catch((e) => {
                console.warn(e);
            });
        }
    }

    async load(optionalMidStepArg?: () => never) {
        if (this.linkedIrrigationSetId) {
            try {
                await MasterIndex.irrigationSet.byIDeventually(this.linkedIrrigationSetId).then((r) => {
                    this.linkedIrrigationSet = r;
                });
            } catch (e) {
                // If the irrigation set is not found, it is likely set as deleted, but still has a reference.
                // We can safely ignore this.
            }
        }
        if (optionalMidStepArg) {
            await optionalMidStepArg();
        }
        RepeatingIrrigationEvent.onNewRepeatingIrrigationEvent.trigger({
            target: this,
        });

        addEntry(this.category, this);

        super.load();
    }

    _eventGeneratingQueue = [];

    /**
     * Deletes this repeating irrigation event
     * @return {Promise<void>}
     */
    async delete() {
        //In the CSV importer, this.ghost is set before deleting so that the delete request is not sent to the server;
        // @ts-ignore
        if (!this.ghost) {
            await RepeatingIrrigationAPI.deleteOne(this.id);
        }
        this.beingDeleted = true;
        const zonesIDs = Object.keys(this.eventInstances);
        for (let i = 0; i < zonesIDs.length; i++) {
            const instance = this.eventInstances[zonesIDs[i]];
            for (let j = 0; j < instance.instances.length; j++) {
                await instance.instances[j].delete().catch((e) => {
                    console.warn(e);
                });
            }
        }
        this.deleted = true;
        await super.delete();
        deleteEntry(this.category, this);
        this.onDelete.trigger({ deleted: this });

        this.onChange.clearListeners();
        this.onDelete.clearListeners();
        this.onLoad.clearListeners();
        this.onDispose.clearListeners();
    }
    dispose() {
        this.onChange.clearListeners();
        this.onDelete.clearListeners();
        this.onLoad.clearListeners();
        this.onDispose.clearListeners();
        super.dispose();
    }

    _lastCachedTimings = {};
    _lastCachedTimingsRRule = "";
    /**
     * Edit repeating event by changing the RRUle. RRULE must be supplied in UTC time.
     * NOTE: this will not execute until all other edit or generateEvents actions have finished.
     * This is a lag-heavy operation, do not call it repeatedly.
     * @param {RRule} newRules
     * @return {Promise<void>}
     */
    async updateInstances(newRules?: RRule) {
        if (this.deleted) {
            return;
        }
        if (!newRules) {
            newRules = this.rule;
        }
        let onDone = undefined;
        const promise = new Promise((resolve) => {
            onDone = resolve;
        });
        if (this.lastEventGenerationPromise) {
            const lastEventGenPromise = this.lastEventGenerationPromise;
            this.lastEventGenerationPromise = promise;
            await lastEventGenPromise;
        } else {
            this.lastEventGenerationPromise = promise;
        }
        try {
            if (this.deleted || this.beingDeleted) {
                onDone();
                return;
            }
            if (this.linkedIrrigationSet) {
                this.zoneIrrigationOffsets = Object.assign(
                    this.zoneIrrigationOffsets,
                    this.linkedIrrigationSet.generateIrrigationOffsets(
                        this.defaultDuration,
                        this.zoneWateringPercentages,
                    ),
                );
            }

            const zones = Object.keys(this.eventInstances).map((zoneID) => {
                if (MasterIndex["zone"].byID[zoneID]) {
                    return MasterIndex["zone"].byID[zoneID];
                } else if (MasterIndex.irrigationSet.byID[zoneID]) {
                    return MasterIndex.irrigationSet.byID[zoneID];
                }
            });
            const allZonesNeeded = Object.keys(this.zoneWateringPercentages || {})
                .map((zoneID) => MasterIndex["zone"].byID[zoneID])
                .filter((z) => z);
            if (this.linkedIrrigationSet) {
                allZonesNeeded.push(this.linkedIrrigationSet);
            }
            allZonesNeeded.forEach((neededZone) => {
                if (!zones.includes(neededZone)) {
                    const template = Object.values(this.eventInstances)[0];
                    const zID = neededZone.id;
                    if (!template) {
                        return;
                    }

                    // @ts-ignore
                    const newInstanceThing = {
                        duration: this.zoneWateringPercentages[zID] * this.defaultDuration,
                        // @ts-ignore
                        startScope: template.startScope,
                        // @ts-ignore
                        endScope: template.endScope,
                        instances: [],
                    };
                    zones.push(neededZone);
                    this.eventInstances[zID] = newInstanceThing;
                }
            });

            let deleted = 0;
            let newInstances = 0;
            let changed = 0;
            const toDelete = [];
            let cachedTimings = {};
            this.rule = newRules;
            if (this.rule.toString() === this._lastCachedTimingsRRule) {
                cachedTimings = this._lastCachedTimings;
            }
            const getTimings = (startScope, endScope) => {
                const cacheKey = new Date(startScope).toString() + "--" + new Date(endScope).toString();
                if (!cachedTimings[cacheKey]) {
                    cachedTimings[cacheKey] = this.rule.between(new Date(startScope), new Date(endScope));
                }
                return cachedTimings[cacheKey].map((v) => new Date(new Date(v).valueOf()));
            };
            const durationOfFullExecution = this.getDurationOfWholeInstance();
            zones.forEach((z) => {
                const zID = z.dbID;
                const instance = this.eventInstances[zID];
                const newTimings = getTimings(new Date(instance.startScope), new Date(instance.endScope));
                let duration = this.zoneWateringPercentages[zID] * this.defaultDuration;
                let set = false;
                if (z instanceof IrrigationSet) {
                    set = true;
                    duration = durationOfFullExecution;
                }
                for (let i = 0; i < Math.max(instance.instances.length, newTimings.length); i++) {
                    //So that if it is deleting a zones instances it doesn't get stuck in an infinite loop
                    if (
                        instance.instances.length === 0 &&
                        (this.zoneWateringPercentages[zID] <= 0 || !this.zoneWateringPercentages[zID]) &&
                        !set
                    ) {
                        break;
                    }
                    if (set && instance.instances.length === 0 && newTimings.length === 0) {
                        break;
                    }
                    if (
                        i < newTimings.length &&
                        ((this.zoneWateringPercentages[zID] && this.zoneWateringPercentages[zID] > 0) || set)
                    ) {
                        const nt = newTimings[i];
                        if (i < instance.instances.length) {
                            const newStartTime = new Date((this.zoneIrrigationOffsets[zID] || 0) + nt.valueOf());
                            if (
                                newStartTime.valueOf() !== instance.instances[i].startTime.valueOf() ||
                                duration !== instance.instances[i].duration
                            ) {
                                changed++;
                                instance.instances[i].startTime = newStartTime;
                                instance.instances[i].duration = duration;
                                instance.instances[i].onChange.trigger({
                                    target: instance.instances[i],
                                    updated: instance.instances[i],
                                });
                                if (
                                    instance.instances[i].currentHashEntry !==
                                    HashedDaySchedule.getHashRecord(newStartTime, zID)
                                ) {
                                    instance.instances[i].currentHashEntry.removeEvent(instance.instances[i]);
                                    instance.instances[i].currentHashEntry = HashedDaySchedule.addEventToHashTable(
                                        instance.instances[i],
                                    );
                                } else {
                                    instance.instances[i].currentHashEntry.flagChanges(instance.instances[i]);
                                }
                            }
                        } else {
                            newInstances++;
                            instance.instances.push(
                                this.generateSingleInstance(
                                    new Date((this.zoneIrrigationOffsets[zID] || 0) + nt.valueOf()),
                                    z,
                                    true,
                                    duration,
                                ),
                            );
                        }
                    } else if (instance.instances.length > 0) {
                        deleted++;
                        toDelete.push(instance.instances[i]);
                        instance.instances.splice(i, 1);
                        i--;
                    } else {
                        break;
                    }
                }
            });
            // newTimings.forEach((nt, i)=>{
            //     if(instance.instances.length > i){
            //         instance.instances[i].startTime = nt;
            //         instance.instances[i].duration = this.zoneWateringPercentages[zID] * this.defaultDuration;
            //     }
            //     else{
            //         instance.instances.push(this.generateSingleInstance(nt, z));
            //     }
            //
            // });});
            this._lastCachedTimings = cachedTimings;
            this._lastCachedTimingsRRule = this.rule.toString();
            toDelete.forEach((td) => {
                td.delete();
            });
            onDone();
            if (changed > 0 || deleted > 0 || newInstances > 0) {
                this.onChange.trigger({ target: this, updated: this });
            }
        } catch (e) {
            console.warn(e);
            console.warn("Error updating instances.");
            onDone();
        }
    }

    async edit(
        zoneWateringPercentages?: Record<string, number>,
        duration?: number,
        rrule?: RRule,
        startTime?: Date,
        zoneIrrigationOffsets?: Record<string, number>,
    ): Promise<RepeatingIrrigationEvent> {
        zoneIrrigationOffsets = zoneIrrigationOffsets || this.zoneIrrigationOffsets;
        zoneWateringPercentages = zoneWateringPercentages || this.zoneWateringPercentages;
        duration = duration || this.duration;
        rrule = rrule || this.rule;
        if (startTime) {
            const rruleOptions = rrule.options;
            const currentStartTime = new Date(rruleOptions.dtstart);
            // @ts-ignore
            const newStartTime = new Date.UTC(
                currentStartTime.getUTCFullYear(),
                currentStartTime.getUTCMonth(),
                currentStartTime.getUTCDate(),
                startTime.getUTCHours(),
                startTime.getUTCMinutes(),
                startTime.getUTCSeconds(),
            );
            rruleOptions.dtstart = newStartTime;
            rrule = new RRule(rruleOptions);
        }

        await RepeatingIrrigationAPI.editOne(
            this.id,
            zoneWateringPercentages,
            duration,
            rrule.toString(),
            zoneIrrigationOffsets,
        ).then((r) => {
            this.zoneWateringPercentages = r.zoneWateringPercentages || this.zoneWateringPercentages;
            this.zoneIrrigationOffsets = r.zoneIrrigationOffsets || this.zoneIrrigationOffsets;
            this.rule = RRule.fromString(r.rrule);
            this.defaultDuration = r.defaultDuration;
            this.updateInstances();
        });
        return this;
    }
    getDurationOfWholeInstance() {
        const possibleDurations = Object.keys(this.zoneWateringPercentages).map((zid) => {
            const attempt =
                (this.zoneIrrigationOffsets[zid] ?? 0) + this.duration * (this.zoneWateringPercentages[zid] ?? 1);
            if (attempt && !isNaN(attempt)) {
                return attempt;
            }
            return 0;
        });
        possibleDurations.push(0);
        possibleDurations.push(0);
        const durationOfFullExecution = Math.max(...possibleDurations);
        return durationOfFullExecution;
    }
    lastEventGenerationPromise = undefined;
    /**
     * Generates individual event objects in the range specified or returns cached ones (or some
     * combination of both).
     * NOTE: this will not execute until all other edit or generateEvents actions have finished.
     * This is a reasonably laggy function if you are actually generating events, please do not call
     * it too frequently.
     * @param {Date} scopeStart
     * @param {Date}  scopeEnd
     * @param {Function}  zoneFilterFunction
     * @constructor
     */
    async generateEvents(scopeStart: Date, scopeEnd: Date, zoneFilterFunction: (z) => boolean = (_): boolean => true) {
        if (this.deleted) {
            return [];
        }

        //This is for generating irrigation set event instances
        const events = [];
        let onDone = undefined;
        const promise = new Promise((resolve) => {
            onDone = resolve;
        });
        if (this.lastEventGenerationPromise) {
            const lastEventGenPromise = this.lastEventGenerationPromise;
            this.lastEventGenerationPromise = promise;
            await lastEventGenPromise;
        } else {
            this.lastEventGenerationPromise = promise;
        }
        if (this.deleted || this.beingDeleted) {
            onDone();
            return [];
        }
        try {
            const zonesToGenerateIn = Object.keys(this.zoneWateringPercentages || {})
                .map((zoneID) => MasterIndex["zone"].byID[zoneID])
                .filter(
                    (z) =>
                        // @ts-ignore
                        z && zoneFilterFunction(z),
                );
            // @ts-ignore
            if (this.linkedIrrigationSet && zoneFilterFunction(this.linkedIrrigationSet)) {
                zonesToGenerateIn.push(this.linkedIrrigationSet);
            }
            if (this.zoneWateringPercentages === null || this.zoneWateringPercentages === undefined) {
                onDone();
                return [];
            }
            const durationOfFullExecution = this.getDurationOfWholeInstance();
            zonesToGenerateIn.forEach((z) => {
                if (!this.eventInstances[z.dbID]) {
                    let duration = undefined;
                    if (z instanceof Zone) {
                        duration = this.zoneWateringPercentages[z.dbID] * this.defaultDuration;
                        // @ts-ignore
                    } else if (z instanceof IrrigationSet) {
                        duration = durationOfFullExecution;
                    }
                    this.eventInstances[z.dbID] = {
                        instances: [],
                        duration: duration,
                        endScope: new Date(scopeEnd.valueOf()),
                        startScope: new Date(scopeStart.valueOf()),
                    };
                } else if (
                    this.eventInstances[z.dbID].endScope.valueOf() >= scopeEnd.valueOf() &&
                    this.eventInstances[z.dbID].startScope.valueOf() <= scopeStart.valueOf()
                ) {
                    return;
                }

                const instance = this.eventInstances[z.dbID];
                let lastRecordedTime = undefined;
                let firstRecordedTime = undefined;
                if (instance.instances.length >= 1) {
                    const lastRecorededEvent = instance.instances[instance.instances.length - 1];
                    lastRecordedTime = new Date(lastRecorededEvent.startTime.valueOf() + lastRecorededEvent.duration);
                    firstRecordedTime = new Date(instance.instances[0].startTime.valueOf());
                }
                const bounds = [scopeStart.valueOf(), scopeEnd.valueOf()];
                if (lastRecordedTime) {
                    bounds[0] = Math.min(lastRecordedTime.valueOf(), bounds[0]);
                    bounds[1] = Math.max(lastRecordedTime.valueOf(), bounds[1]);
                }
                if (firstRecordedTime) {
                    bounds[0] = Math.min(firstRecordedTime.valueOf(), bounds[0]);
                    bounds[1] = Math.max(firstRecordedTime.valueOf(), bounds[1]);
                }
                let allNewTimes = [];

                if (firstRecordedTime && lastRecordedTime) {
                    const prependTimes = this.rule.between(new Date(bounds[0]), firstRecordedTime);
                    const appendTimes = this.rule.between(lastRecordedTime, new Date(bounds[1]));
                    allNewTimes = [...prependTimes, ...appendTimes];
                } else {
                    allNewTimes = this.rule.between(new Date(bounds[0]), new Date(bounds[1]));
                }
                allNewTimes = allNewTimes.map((v) => new Date(new Date(v).valueOf()));
                allNewTimes.forEach((ni) => {
                    let newInstance = undefined;
                    if (z instanceof Zone) {
                        newInstance = this.generateSingleInstance(
                            new Date((this.zoneIrrigationOffsets[z.dbID] || 0) + ni.valueOf()),
                            z,
                        );
                        // @ts-ignore
                    } else if (z instanceof IrrigationSet) {
                        newInstance = this.generateSingleInstance(
                            new Date(ni.valueOf()),
                            z,
                            true,
                            durationOfFullExecution,
                        );
                    }
                    if (lastRecordedTime && firstRecordedTime) {
                        if (ni.valueOf() > lastRecordedTime.valueOf()) {
                            instance.instances.push(newInstance);
                        } else if (ni.valueOf() < firstRecordedTime.valueOf()) {
                            instance.instances.splice(0, 0, newInstance); //push(newInstance);
                        } else {
                            console.warn("Could not insert instance");
                        }
                    } else {
                        instance.instances.push(newInstance);
                    }
                });

                let firstEventToReturn = instance.instances.length - 1;
                let lastEventToReturn = instance.instances.length - 1;
                for (let i = 0; i < instance.instances.length; i++) {
                    if (instance.instances[i].startTime.valueOf() > scopeStart.valueOf() && i < firstEventToReturn) {
                        firstEventToReturn = i;
                    }
                    if (
                        instance.instances[i].startTime.valueOf() + instance.instances[i].duration >
                        scopeEnd.valueOf()
                    ) {
                        lastEventToReturn = i;
                        break;
                    }
                }
                events.push(...instance.instances.slice(firstEventToReturn, lastEventToReturn + 1));
                instance.startScope = new Date(bounds[0]);
                instance.endScope = new Date(bounds[1]);
            });
            onDone();
            return events;
        } catch (e) {
            console.warn("error generating events");
            console.warn(e);
            onDone();
        }
    }

    generateSingleInstance(time, zone, loadImmediately = true, duration?: number) {
        let newEvent;
        if (zone instanceof Zone) {
            newEvent = new IrrigationEventProto(undefined, true);
            newEvent.type = "single";
        } else if (zone instanceof IrrigationSet) {
            newEvent = new SetIrrigationEvent(undefined, true);
            newEvent.set = zone;
        }
        newEvent.dbID = generateUID(this.uid + "RepInst");
        newEvent.duration = duration ?? this.zoneWateringPercentages[zone.dbID] * this.defaultDuration;

        newEvent.startTime = time;
        newEvent.commandGroup = this;
        newEvent.zone = zone;
        if (newEvent.type === "set") {
            if (isNaN(newEvent.duration)) {
                console.warn("new event non-number duration");
            }
        }
        if (loadImmediately) {
            newEvent.load().then(() => {
                //pass
            });
        }
        return newEvent;
    }
}
